import React, { FC, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import HorizontalSelector from 'views/components/Selectors/HorizontalSelector'
import BrainMap from 'views/components/Graphics/BrainMap'
import TeamMapGrid from '../../../../containers/Grids/TeamMapGrid'
import { Container, StandardButton, Title, UsersButtonContainer } from './styled'
import {
  ContentCard,
  EnvironmentBanner,
  EnvironmentText,
  EnvironmentTitle,
  InsightPill,
  InsightPills,
  InsightSection,
  InsightSectionTitle,
  InsightsStack,
  SummaryCard,
  ContextNote,
  ContextNoteText,
  SummaryGrid,
  SummaryHint,
  SummaryLabel,
  SummaryValue,
} from 'views/containers/TeamMap/styled'
import { useNotification } from 'lib/context/notification.context'
import iconInfo from '../../../../components/assets/iconInfo.png'
import {
  NoDataContainer,
  NoDataIcon,
  NoDataSubContainer,
  NoDataText,
  TextContainer,
} from 'views/screens/Platform-administrator/Analytics/styled'
import ModalLoader from 'views/components/UI/ModalLoader/modal-loader'
import { MainLayout } from 'views/layouts'
import BrainGraphicContainer from 'views/containers/TeamMap/BrainGraphicContainer'
import { BetesaResultsResponse } from 'views/containers/TeamMap/store/entities'
import { useLocation, useNavigate } from 'react-router-dom'
import { OPTIONS_DATA, optionsMap } from '../Constants/Constants'
import { platformAdminRoutes } from 'router/routes'
import { BrainResultsResponse, QuadrantResults } from '../Entities'
import { useTeamMap } from '../Hooks/useTeamMap'

export interface TeamMapProps {
  betesaMapId?: string
  usersByQuadrant?: QuadrantResults
  talents?: boolean
}

const QUADRANT_DETAILS = {
  FI: {
    shortName: 'FI',
    displayName: 'Frontal izquierdo',
    summary: 'analisis estructurado y pensamiento critico',
    environment: 'Entornos con objetivos claros, espacios de analisis y decisiones soportadas en datos.',
  },
  FD: {
    shortName: 'FD',
    displayName: 'Frontal derecho',
    summary: 'vision, estrategia y cambio',
    environment: 'Entornos con retos nuevos, espacio para idear y margen para explorar alternativas.',
  },
  BI: {
    shortName: 'BI',
    displayName: 'Basal izquierdo',
    summary: 'orden, continuidad y control operativo',
    environment: 'Entornos con estabilidad operativa, seguimiento disciplinado y claridad de metodo.',
  },
  BD: {
    shortName: 'BD',
    displayName: 'Basal derecho',
    summary: 'relacionamiento, servicio y cohesion',
    environment: 'Entornos colaborativos, cercanos al cliente y con alta coordinacion entre personas.',
  },
} as const

const ALERT_COPY: Record<string, string> = {
  E: 'Predomina un estilo estable y consistente.',
  EI: 'Se observa una combinacion entre estabilidad e impulso.',
  'EE:II': 'El equipo mezcla respuestas opuestas; conviene alinear prioridades.',
  IE: 'Hay señales de exigencia alta con necesidad de contencion.',
  I: 'El equipo muestra tension sostenida; conviene revisar cargas y acompanamiento.',
}

const TAB_DESCRIPTIONS: Record<string, string> = {
  '2': 'Dominancia muestra en qué cuadrante tiene cada evaluado su predominio principal.',
  '3': 'Talentos muestra los evaluados que tienen ese cuadrante en un nivel alto de desarrollo, es decir, superior a 80 puntos.',
}

type QuadrantCode = keyof typeof QUADRANT_DETAILS

const getQuadrantOrder = (usersList: any[]) => {
  const base = { FI: 0, FD: 0, BI: 0, BD: 0 }

  usersList.forEach((user) => {
    const dominance = user.domination as QuadrantCode
    if (dominance in base) {
      base[dominance] += 1
    }
  })

  return Object.entries(base).sort(([, a], [, b]) => b - a) as Array<[QuadrantCode, number]>
}

const getAlertOrder = (usersList: any[]) => {
  const base = usersList.reduce<Record<string, number>>((acc, user) => {
    const key = user.alert_level || 'Sin dato'
    acc[key] = (acc[key] || 0) + 1
    return acc
  }, {})

  return Object.entries(base).sort(([, a], [, b]) => b - a)
}

const TeamMapDevelopment: FC<TeamMapProps> = () => {
  const { getError } = useNotification()
  const location = useLocation()
  const navigate = useNavigate()
  const { mapName, teamMapId: betesaMapId } = location.state
  const { getTeamMap } = useTeamMap()
  const [selectedOption, setSelectedOption] = useState<string>('1')
  const [betesaResults, setBetesaResults] = useState<BetesaResultsResponse>({} as BetesaResultsResponse)
  const [page, setPage] = useState<number>(1)
  const [usersQuadrants, setUsersQuadrants] = useState<QuadrantResults>({} as QuadrantResults)
  const [talentQuadrants, setTalentQuadrants] = useState<QuadrantResults>({} as QuadrantResults)
  const [planQuadrants, setPlanQuadrants] = useState<QuadrantResults>({} as QuadrantResults)
  const [brainResponse, setBrainResponse] = useState<BrainResultsResponse>({} as BrainResultsResponse)
  const [loading, setLoading] = useState<boolean>(false)
  const users = useMemo(() => betesaResults?.users || [], [betesaResults])

  const teamInsights = useMemo(() => {
    if (!users.length) {
      return null
    }

    const totalUsers = users.length
    const quadrantRanking = getQuadrantOrder(users)
    const alertRanking = getAlertOrder(users)
    const planRanking = Object.entries(
      users.reduce<Record<string, number>>((acc, user) => {
        const key = user.quadrant_develop || 'Sin plan'
        acc[key] = (acc[key] || 0) + 1
        return acc
      }, {})
    ).sort(([, a], [, b]) => b - a)

    const [topQuadrant, topQuadrantCount] = quadrantRanking[0]
    const topQuadrantShare = Math.round((topQuadrantCount / totalUsers) * 100)
    const lowestQuadrantCount = quadrantRanking[quadrantRanking.length - 1][1]
    const spread = topQuadrantCount - lowestQuadrantCount
    const topAlert = alertRanking[0]
    const topAlertShare = topAlert ? Math.round((topAlert[1] / totalUsers) * 100) : 0
    const topPlan = planRanking[0]
    const topAlertDescription = topAlert ? ALERT_COPY[topAlert[0]] || `Predomina la alerta ${topAlert[0]}.` : ''

    let balanceTitle = 'Balanceado'
    let balanceHint = 'La distribucion permite combinar estilos sin una dominancia excesiva.'

    if (spread <= 1) {
      balanceTitle = 'Muy balanceado'
      balanceHint = 'La presencia entre cuadrantes es pareja y favorece la complementariedad.'
    } else if (topQuadrantShare >= 50) {
      balanceTitle = 'Concentrado'
      balanceHint = `Hay una carga fuerte hacia ${QUADRANT_DETAILS[topQuadrant].displayName.toLowerCase()}.`
    } else if (topQuadrantShare >= 40) {
      balanceTitle = 'Con sesgo moderado'
      balanceHint = `Se nota una preferencia por ${QUADRANT_DETAILS[topQuadrant].summary}.`
    }

    let primaryRisk = 'No se observan desequilibrios criticos en la lectura general.'

    if (topQuadrantShare >= 50) {
      primaryRisk = `Alta concentracion en ${QUADRANT_DETAILS[topQuadrant].shortName}; el equipo puede perder diversidad al decidir.`
    } else if (topAlert && topAlert[0] === 'I') {
      primaryRisk = 'El nivel de alerta dominante sugiere tension sostenida en el equipo.'
    } else if (topAlertShare >= 40 && topAlert) {
      primaryRisk = `Predomina la alerta ${topAlert[0]}; conviene revisar presion, ritmo y acompanamiento.`
    } else if (topPlan && topPlan[0] !== 'Sin plan' && Number(topPlan[1]) / totalUsers >= 0.45) {
      primaryRisk = `Muchas personas convergen en el plan ${topPlan[0]}; puede haber una brecha comun de desarrollo.`
    }

    const alerts = [
      `Predominio ${QUADRANT_DETAILS[topQuadrant].shortName}: ${topQuadrantShare}% del equipo`,
      topAlert ? `Alerta dominante ${topAlert[0]}: ${topAlertShare}%. ${topAlertDescription}` : null,
      topPlan && topPlan[0] !== 'Sin plan' ? `Plan mas recurrente: ${topPlan[0]}` : 'Aun no hay un plan dominante de desarrollo',
    ].filter(Boolean) as string[]

    const recommendations = [
      topQuadrantShare >= 45
        ? `Sumar perfiles complementarios a ${QUADRANT_DETAILS[topQuadrant].shortName} en decisiones clave y comites.`
        : 'Mantener equipos mixtos al asignar proyectos de alta prioridad.',
      topAlert && topAlert[0] === 'I'
        ? 'Priorizar seguimiento de carga, conversaciones de cuidado y claridad de expectativas.'
        : 'Usar esta lectura para distribuir mejor roles, ritmos y responsabilidades.',
      topPlan && topPlan[0] !== 'Sin plan'
        ? `Alinear acciones de desarrollo alrededor de ${topPlan[0]} sin perder personalizacion.`
        : 'Consolidar un frente comun de desarrollo cuando el proceso tenga mas avance.',
    ]

    return {
      predominanceTitle: `${QUADRANT_DETAILS[topQuadrant].shortName} · ${QUADRANT_DETAILS[topQuadrant].displayName}`,
      predominanceHint: `${topQuadrantShare}% del equipo se concentra en ${QUADRANT_DETAILS[topQuadrant].summary}.`,
      balanceTitle,
      balanceHint,
      primaryRisk,
      environmentText:
        topQuadrantShare <= 35
          ? 'El equipo responde mejor en entornos mixtos, con retos diversos y colaboracion entre estilos.'
          : QUADRANT_DETAILS[topQuadrant].environment,
      alerts,
      recommendations,
    }
  }, [users])

  useEffect(() => {
    setLoading(true)

    getTeamMap(betesaMapId)
      .then((data: any) => {
        setBetesaResults({ users: data } as BetesaResultsResponse)
        const usersFIDomination = data.filter((user: any) => user.domination === 'FI')
        const usersBIDomination = data.filter((user: any) => user.domination === 'BI')
        const usersBDDomination = data.filter((user: any) => user.domination === 'BD')
        const usersFDDomination = data.filter((user: any) => user.domination === 'FD')

        const usersFIplan = data.filter((user: any) => user.quadrant_develop === 'FI')
        const usersBIplan = data.filter((user: any) => user.quadrant_develop === 'BI')
        const usersBDplan = data.filter((user: any) => user.quadrant_develop === 'BD')
        const usersFDplan = data.filter((user: any) => user.quadrant_develop === 'FD')

        const usersFItalent = data.filter((user: any) => user.FI >= 80)
        const usersBITalent = data.filter((user: any) => user.BI >= 80)
        const usersBDTalent = data.filter((user: any) => user.BD >= 80)
        const usersFDTalent = data.filter((user: any) => user.FD >= 80)

        setUsersQuadrants({
          fi: usersFIDomination,
          bi: usersBIDomination,
          bd: usersBDDomination,
          fd: usersFDDomination,
        })
        setTalentQuadrants({
          fi: usersFItalent,
          bi: usersBITalent,
          bd: usersBDTalent,
          fd: usersFDTalent,
        })
        setPlanQuadrants({
          fi: usersFIplan,
          bi: usersBIplan,
          bd: usersBDplan,
          fd: usersFDplan,
        })

        const calculateAverage = (usersList: any[], key: string) => {
          const total = usersList.reduce((acc, user) => Number(acc) + Number(user[key]), 0)
          return usersList.length ? total / usersList.length : 0
        }

        const averageFI = Math.floor(calculateAverage(data, 'FI'))
        const averageBI = Math.floor(calculateAverage(data, 'BI'))
        const averageBD = Math.floor(calculateAverage(data, 'BD'))
        const averageFD = Math.floor(calculateAverage(data, 'FD'))

        setBrainResponse({
          FI: averageFI,
          BI: averageBI,
          BD: averageBD,
          FD: averageFD,
        })
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
        getError('Ha ocurrido un error al cargar los usuarios')
      })
  }, [betesaMapId, getError, page, getTeamMap])

  useLayoutEffect(() => {
    setLoading(true)
  }, [selectedOption])

  const handleManageUsers = () => {
    navigate(platformAdminRoutes.TeamDevelopmentUsers, { state: { manageUsers: betesaResults, mapName, betesaMapId } })
  }

  return (
    <MainLayout>
      <Container>
        <Title>Mapa - {mapName}</Title>
        <UsersButtonContainer>
          <StandardButton onClick={handleManageUsers}>Administrar usuarios</StandardButton>
        </UsersButtonContainer>
        <HorizontalSelector items={optionsMap} setSelected={() => void 0} selected="1" />

        {Object.keys(betesaResults).length > 0 ? (
          <div>
            <HorizontalSelector items={OPTIONS_DATA} selected={selectedOption} setSelected={setSelectedOption} appearance="segmented" />
            {(selectedOption === '2' || selectedOption === '3') && (
              <ContextNote>
                <ContextNoteText>{TAB_DESCRIPTIONS[selectedOption]}</ContextNoteText>
              </ContextNote>
            )}
            {selectedOption === '1' && (
              <>
                {teamInsights && (
                  <InsightsStack>
                    <SummaryGrid>
                      <SummaryCard>
                        <SummaryLabel>Predominio del equipo</SummaryLabel>
                        <SummaryValue>{teamInsights.predominanceTitle}</SummaryValue>
                        <SummaryHint>{teamInsights.predominanceHint}</SummaryHint>
                      </SummaryCard>
                      <SummaryCard>
                        <SummaryLabel>Balance del equipo</SummaryLabel>
                        <SummaryValue>{teamInsights.balanceTitle}</SummaryValue>
                        <SummaryHint>{teamInsights.balanceHint}</SummaryHint>
                      </SummaryCard>
                      <SummaryCard>
                        <SummaryLabel>Riesgo principal</SummaryLabel>
                        <SummaryValue>Lectura ejecutiva</SummaryValue>
                        <SummaryHint>{teamInsights.primaryRisk}</SummaryHint>
                      </SummaryCard>
                    </SummaryGrid>

                    <EnvironmentBanner>
                      <EnvironmentTitle>Indicador de entorno recomendado</EnvironmentTitle>
                      <EnvironmentText>{teamInsights.environmentText}</EnvironmentText>
                    </EnvironmentBanner>

                    <InsightSection>
                      <InsightSectionTitle>Alertas del equipo</InsightSectionTitle>
                      <InsightPills>
                        {teamInsights.alerts.map((alert) => (
                          <InsightPill key={alert}>{alert}</InsightPill>
                        ))}
                      </InsightPills>
                    </InsightSection>

                    <InsightSection>
                      <InsightSectionTitle>Recomendaciones accionables</InsightSectionTitle>
                      <InsightPills>
                        {teamInsights.recommendations.map((recommendation) => (
                          <InsightPill key={recommendation}>{recommendation}</InsightPill>
                        ))}
                      </InsightPills>
                    </InsightSection>
                  </InsightsStack>
                )}

                <ContentCard>
                  <TeamMapGrid results={betesaResults} handlePagination={setPage} />
                </ContentCard>
              </>
            )}
            {selectedOption === '2' && <BrainMap usersByQuadrant={usersQuadrants} variant="dominance" />}
            {selectedOption === '3' && <BrainMap usersByQuadrant={talentQuadrants} talents variant="talents" />}
            {selectedOption === '4' && (
              <div
                style={{
                  marginTop: '20px',
                  width: '100%',
                }}
              >
                <div>
                  {Object.keys(brainResponse).length > 0 ? (
                    <BrainGraphicContainer
                      FI={brainResponse.FI}
                      FD={brainResponse.FD}
                      BD={brainResponse.BD}
                      BI={brainResponse.BI}
                    />
                  ) : (
                    <NoDataContainer>
                      <NoDataSubContainer>
                        <NoDataIcon src={iconInfo} />
                        <TextContainer>
                          <NoDataText>Error al cargar los datos del perfil ideal</NoDataText>
                        </TextContainer>
                      </NoDataSubContainer>
                    </NoDataContainer>
                  )}
                </div>
              </div>
            )}
            {selectedOption === '5' && <BrainMap usersByQuadrant={planQuadrants} variant="plan" />}
          </div>
        ) : betesaMapId ? (
          <NoDataContainer>
            <NoDataSubContainer>
              <NoDataIcon src={iconInfo} />
              <TextContainer>
                <NoDataText>No hay datos de Betesa en este proceso</NoDataText>
              </TextContainer>
            </NoDataSubContainer>
          </NoDataContainer>
        ) : !loading ? (
          <NoDataContainer>
            <NoDataSubContainer>
              <NoDataIcon src={iconInfo} />
              <TextContainer>
                <NoDataText>Los usuarios no han contestado la prueba</NoDataText>
              </TextContainer>
            </NoDataSubContainer>
          </NoDataContainer>
        ) : (
          <ModalLoader isLoading={loading} />
        )}
      </Container>
    </MainLayout>
  )
}

export default TeamMapDevelopment
